import { useState } from 'react'
import { Bell, CheckCheck, ChevronDown, ChevronUp } from 'lucide-react'
import type { AirAlert } from '@/types/airTraffic'
import { AIR, FONT_MONO } from './theme'

const SEV_COLOR: Record<AirAlert['severity'], string> = {
  critical: AIR.emergency,
  high: AIR.military,
  medium: AIR.watched,
  info: AIR.muted,
}

const KIND_LABEL: Record<AirAlert['kind'], string> = {
  emergency: 'Squawk',
  rule: 'Watch',
  aoi_military: 'AOI',
}

function timeOf(ts: string) {
  return new Date(ts).toISOString().slice(11, 16)
}

interface Props {
  alerts: AirAlert[]
  unread: number
  onOpen: (a: AirAlert) => void
  onReadAll: () => void
}

/** Emergency squawks, watch-rule hits and military-near-AOI alerts. Clicking one
 * flies the map there and opens the aircraft. */
export default function AlertTicker({ alerts, unread, onOpen, onReadAll }: Props) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="relative shrink-0 border-t" style={{ background: AIR.panel, borderColor: AIR.line }}>
      {expanded && (
        <div
          className="absolute bottom-full left-0 right-0 z-20 max-h-[320px] overflow-y-auto border-t shadow-2xl"
          style={{ background: AIR.panel, borderColor: AIR.lineStrong }}
        >
          {alerts.length === 0 && (
            <div className="px-4 py-3 text-[12px]" style={{ color: AIR.muted }}>
              No alerts in the last 72 hours. Emergency squawks and military aircraft near your GEOINT AOIs alert automatically;
              add watch rules for anything else.
            </div>
          )}
          {alerts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(a)}
              className="flex w-full items-start gap-3 border-b px-4 py-2 text-left hover:bg-white/[0.03]"
              style={{ borderColor: AIR.line }}
            >
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: SEV_COLOR[a.severity], opacity: a.read ? 0.4 : 1 }} />
              <span className="w-12 shrink-0 text-[11px] tabular-nums" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                {timeOf(a.ts)}
              </span>
              <span className="min-w-0">
                <span className="block text-[12px]" style={{ color: a.read ? AIR.muted : AIR.bright }}>
                  {a.title}
                </span>
                <span className="block truncate text-[11px]" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                  {a.detail}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="flex h-9 items-center gap-2 px-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex shrink-0 items-center gap-1.5 text-[11px]"
          style={{ color: unread ? AIR.bright : AIR.muted }}
          aria-expanded={expanded}
        >
          <Bell className="h-3.5 w-3.5" style={{ color: unread ? AIR.military : AIR.muted }} />
          Alerts
          {unread > 0 && (
            <span className="rounded-sm px-1 text-[10px] font-semibold" style={{ background: AIR.military, color: AIR.bg }}>
              {unread}
            </span>
          )}
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        </button>
        <div className="h-4 w-px shrink-0" style={{ background: AIR.line }} />
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none]">
          {alerts.length === 0 && (
            <span className="text-[11px]" style={{ color: AIR.dim }}>
              Quiet. Watching for emergency squawks, watch-rule hits and military aircraft near your AOIs.
            </span>
          )}
          {alerts.slice(0, 12).map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(a)}
              className="flex shrink-0 items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] hover:bg-white/[0.04]"
              style={{ boxShadow: `inset 0 0 0 1px ${SEV_COLOR[a.severity]}${a.read ? '33' : '88'}`, color: a.read ? AIR.muted : AIR.text }}
              title={a.detail}
            >
              <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: SEV_COLOR[a.severity] }}>
                {KIND_LABEL[a.kind]}
              </span>
              <span style={{ fontFamily: FONT_MONO }}>{a.cs || a.reg || a.hex.toUpperCase()}</span>
              <span style={{ color: AIR.muted, fontFamily: FONT_MONO }}>{timeOf(a.ts)}</span>
            </button>
          ))}
        </div>
        {unread > 0 && (
          <button type="button" onClick={onReadAll} className="flex shrink-0 items-center gap-1 text-[11px]" style={{ color: AIR.muted }}>
            <CheckCheck className="h-3.5 w-3.5" /> Mark read
          </button>
        )}
      </div>
    </div>
  )
}
